import React, { useMemo } from 'react';

import { IntlProvider } from 'react-intl';

import { useMessages } from '../services/use-messages';
import { useSafeIntl } from '../services/use-safe-intl';

import { type IntlMessagesProviderProps } from './types';

export default function IntlMessagesProvider({
	loaderFn,
	children,
	defaultMessages,
}: IntlMessagesProviderProps): React.JSX.Element {
	const intl = useSafeIntl();
	const messages = useMessages(intl.locale, loaderFn);

	/**
	 * IntlProvider does not inherit from upstream IntlProviders,
	 * we lookup messages in the Intl context (via useSafeIntl) and pass them downstream
	 * This prevents the missing messages error.
	 */
	const mergedMessages = useMemo(() => {
		return { ...defaultMessages, ...intl.messages, ...messages };
	}, [intl.messages, messages, defaultMessages]);

	return (
		<IntlProvider
			defaultLocale="en"
			locale={intl.locale}
			onError={intl.onError}
			messages={mergedMessages}
		>
			{children}
		</IntlProvider>
	);
}
